import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Gender } from '~/pages/CreateNewCharacter';
import { DetailInputProps } from '~/components/CreateNewCharacter/DetailInput/DetailInput';
import { saveAvatar } from '~/api';
import { useCreateCharacterMutation } from '~/features/character';
import userKey from '~/features/user/userKey';
import {
  CreateCharacterRequest,
  ModalParams,
  SaveAvatarRequest,
} from '~/types';
import { convertWebPToPng, GAEvent, getUuidV4 } from '~/utils';
import { GA, PAGE_ROUTE } from '~/constants';
import { MEGABYTE } from '~/constants/size';
import { useUserStore } from '~/store';
import useCreateNewCharacterStore from '~/store/useCreateNewCharacterStore';
import useToast from '~/store/useToast';
import useDirectionInput from '../SetProfile/useDirectionInput';
import useModal from '../useModal';

const PHOTO_CAPACITY = 10;
const WEBP_HEADER_HEX = '52494646';

export type CommonInput = {
  characterName: string;
  opening: string;
  tagLine: string;
  persona: string;
};

interface useDetailInputProps {
  onNextStep: DetailInputProps['onNextStep'];
  gender?: Gender;
}

const useDetailInput = ({ onNextStep, gender }: useDetailInputProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const email = useUserStore((state) => state.email);
  const abortControllerRef = useRef<AbortController>();

  const [profileFile, setProfileFile] = useState<File>();
  const [profileImage, setProfileImage] = useState<string>();

  const [commonInput, setCommonInput] = useState<CommonInput>({
    characterName: '',
    opening: '',
    tagLine: '',
    persona: '',
  });
  const [showPersona, setShowPersona] = useState(false);
  const { textRef, handleDirectionClick, handleUserNameClick } =
    useDirectionInput({
      setInput: setCommonInput,
    });

  const { characterName, opening, tagLine, persona } = commonInput;
  const [isPending, setIsPending] = useState(false);

  const uploadType = useCreateNewCharacterStore((state) => state.generateType);
  const { customizeUploadUrl } = useCreateNewCharacterStore((state) => ({
    customizeUploadUrl: state.customizeUpload.uploadUrl,
  }));
  const { presetCharacterId } = useCreateNewCharacterStore((state) => ({
    presetCharacterId: state.preset.id,
  }));
  const { finalAvaturnId, finalAvaturnUrl } = useCreateNewCharacterStore(
    (state) => ({
      finalAvaturnId: state.customizeAvaturn.result.characterId,
      finalAvaturnUrl: state.customizeAvaturn.result.uploadUrl,
    }),
  );
  const { mutate: createCharacterMutate, isPending: createCharacterPending } =
    useCreateCharacterMutation();

  const { setFinalGenerateState } = useCreateNewCharacterStore((state) => ({
    setFinalGenerateState: state.setFinalGenerateState,
  }));

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => abortControllerRef.current?.abort();
  }, []);

  const handleDeleteImageClick = () => {
    setProfileFile(undefined);
    setProfileImage(undefined);
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // const extension = file.name.split('.').pop()?.toLowerCase();
    const fileExtension = file.name.toLowerCase();
    const allowedExtensions = ['.png', '.jpg', '.jpeg'];

    if (!allowedExtensions.some((ext) => fileExtension.endsWith(ext))) {
      showToast('error', 'The file format can only be JPG, JPEG, PNG files.');
      return;
    }

    if (file.size >= PHOTO_CAPACITY * MEGABYTE) {
      showToast('error', `cannot upload over ${PHOTO_CAPACITY}MB`);
      return;
    }

    const blob = file.slice(0, 4);
    const fileReader = new FileReader();

    try {
      const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        fileReader.onload = () => resolve(fileReader.result as ArrayBuffer);
        fileReader.onerror = reject;
        fileReader.readAsArrayBuffer(blob);
      });

      const arr = new Uint8Array(buffer);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }
      if (header.startsWith(WEBP_HEADER_HEX)) {
        try {
          const convertedFile = await convertWebPToPng(file);
          setProfileFile(convertedFile);
          const reader = new FileReader();
          reader.onload = () => {
            if (reader.result && typeof reader.result === 'string') {
              setProfileImage(reader.result);
            }
          };
          reader.readAsDataURL(convertedFile);
          showToast('success', 'WebP image has been converted to PNG format');
          return;
        } catch (error) {
          console.error('Failed to convert WebP to PNG:', error);
          showToast(
            'error',
            'Failed to convert WebP image. Please upload a PNG or JPG file directly.',
          );
          return;
        }
      }
    } catch (error) {
      console.error('An error occurred while checking the file:', error);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        if (typeof reader.result !== 'string') return;
        setProfileFile(file);
        setProfileImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleApplyClick = async () => {
    try {
      setIsPending(true);

      if (!profileFile || uploadType === '') return;

      const characterId =
        uploadType === 'avaturn'
          ? finalAvaturnId
          : uploadType === 'user'
          ? getUuidV4()
          : uploadType === 'preset'
          ? presetCharacterId
          : '';

      if (uploadType !== 'preset') {
        await handleSaveAvatar(characterId);
      }

      const requestData: CreateCharacterRequest = {
        email,
        profile_image: profileFile,
        avatar_id: characterId,
        character_name: characterName,
        opening,
        tag_line: tagLine,
        persona,
        show_persona: showPersona ? '1' : '0',
        generate_type: uploadType,
        character_type: 'user',
        llm_type: '',
      };

      // send GA event : CLICK_APPLY_CHARACTER
      GAEvent(email, GA.ACTION.CLICK_APPLY_CHARACTER, {
        character_id: requestData.avatar_id,
        character_name: requestData.character_name,
      });

      await new Promise((resolve, reject) => {
        createCharacterMutate(requestData, {
          onSuccess({ data }) {
            queryClient.invalidateQueries({
              queryKey: userKey.getMyCreation(),
            });

            if (data.generate_type === 'preset') {
              setFinalGenerateState({
                type: 'preset',
                characterId: data.character_id,
                characterName: characterName,
              });

              onNextStep();
            } else if (data.generate_type === 'user') {
              setFinalGenerateState({
                type: 'user',
                characterName: characterName,
              });

              onNextStep();
            } else if (data.generate_type === 'avaturn') {
              setFinalGenerateState({
                type: 'avaturn',
                characterId: data.character_id,
                characterName: characterName,
              });

              onNextStep();
            }
            resolve(data);
          },
          onError: (error) => {
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error(error);
      showToast('error', 'server error');
      abortControllerRef.current = new AbortController();
    } finally {
      setIsPending(false);
    }
  };

  const handleSaveAvatar = async (characterId: string) => {
    try {
      if (profileFile) {
        const url =
          uploadType === 'avaturn'
            ? finalAvaturnUrl
            : uploadType === 'user'
            ? customizeUploadUrl
            : '';
        const thumbnail = uploadType === 'user' ? profileFile : '';

        const requestData: SaveAvatarRequest = {
          email,
          avaturn_id: characterId, // 사용자 업로드 파일일 경우 uuid
          model_file_path: url,
          thumbnail_file: thumbnail, // 파일객체
          gender: gender === 'male' ? 'M' : 'F',
        };

        if (requestData) {
          const formData = new FormData();

          for (const [key, value] of Object.entries(requestData)) {
            formData.append(key, value);
          }

          const { data, status } = await saveAvatar(formData);

          if (status !== 200) {
            throw new Error('server error');
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleHeaderCloseClick = () => {
    const modalData: ModalParams = {
      title: 'Leave page',
      content: 'Are you sure you want to exit without saving?',
      type: 'double_red',
      click: () => {
        navigate(PAGE_ROUTE.MAIN, {
          replace: true,
        });
      },
      buttonName: 'Leave',
    };

    openModal(modalData);
  };

  const handleProcessCancelClick = () => {
    abortControllerRef.current?.abort();
  };

  const GenerateButtonDisabled =
    !profileFile ||
    !commonInput.characterName ||
    !commonInput.opening ||
    !commonInput.tagLine ||
    !commonInput.persona;

  return {
    profileImage,
    setProfileImage,
    commonInput,
    setCommonInput,
    showPersona,
    setShowPersona,
    profileFile,
    handleDeleteImageClick,
    handleImageChange,
    textRef,
    handleUserNameClick,
    handleDirectionClick,
    GenerateButtonDisabled,
    uploadType,
    isPending,
    createCharacterPending,
    handleApplyClick,
    handleHeaderCloseClick,
    handleProcessCancelClick,
  };
};

export default useDetailInput;
